import {PermissionKey} from "@bounce/shared/enums/permissions";
import {componentLoader, Loadable} from "@bounce/shared/helpers";
import {lazy} from "react";
import {Navigate, RouteObject} from "react-router-dom";
import BackofficeLayout from "layout/agent";
import SpringLayout from "layout/spring/SpringLayout";
import LoginPage from "pages/backoffice/common/LoginPage";
import {backofficeUrls as urls} from "./backofficeUrls";
import BackofficeGuard from "./guards/BackofficeGuard";
import {PermissionGuard} from "./guards/PermissionGuard";
// Backoffice lazy-loaded components

const PermissionRequired = Loadable(
  lazy(() => componentLoader(() => import("../../pages/permission-required/PermissionRequiredPage")))
);
const Search = Loadable(lazy(() => componentLoader(() => import("../../pages/backoffice/search/Search"))));
const RexHistory = Loadable(
  lazy(() => componentLoader(() => import("../../pages/backoffice/rex-conversations/RexConversations")))
);
const InformationPanel = Loadable(
  lazy(() => componentLoader(() => import("../../pages/backoffice/information-panel/InformationPanel")))
);
const TargetBreakdown = Loadable(
  lazy(() => componentLoader(() => import("../../pages/backoffice/target-breakdown/TargetBreakdown")))
);

// Spring lazy-loaded components

const UserLevelMedia = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-operations/UserLevelMedia")))
);
const BatchLevelMedia = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-operations/media-batch/BatchLevelMedia")))
);
const AccountImports = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-operations/AccountImports")))
);
const AccountManagement = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-operations/AccountManagement")))
);
const Washing = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/partner-operations/Washing"))));
const Disputes = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/disputes/Disputes"))));
const DisputesDashboard = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/disputes/Dashboard"))));
const DocumentExport = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/document-export/DocumentExport")))
);
const DocumentRequests = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/document-requests/DocumentRequests")))
);
const ImportPayments = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-operations/ImportPayments")))
);
const ImportPricing = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/pricing/ImportPricing"))));
const GeneratePricingWashReport = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/pricing/GeneratePricingWashReport")))
);
const BlockedNumbers = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/call-center/blocked-numbers/BlockedNumbers")))
);
const RExEmployees = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/call-center/employees-management/EmployeesPage")))
);
const ShiftManagement = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/call-center/shift-management/ShiftManagement")))
);
const Simulation = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/call-center/simulation/Simulation")))
);
const TaskMonitor = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/tasks/TaskMonitor"))));
const Bankruptcies = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/bankruptcies/BankruptciesPanel")))
);
const Inventory = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/inventory/Inventory"))));
const LegalCollectionsBulkOperation = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/legal-collections/LegalCollectionsBulkOperation")))
);
const BulkOperations = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/dsc/bulk-operations/BulkOperations")))
);
const RepresentationCompanies = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/representation-companies/RepresentationCompanies")))
);
const PartnersList = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/partners/PartnersList"))));
const PaymentsCredentialsList = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/payments-credentials/PaymentsCredentialsList")))
);
const AddPaymentsCredentials = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/payments-credentials/AddPaymentsCredentials")))
);
const AddPartner = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/partners/AddPartner"))));
const EditPartner = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/partners/EditPartner"))));
const AnonymousCommunications = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/dsc/AnonymousCommunications")))
);
const DSCOverview = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/dsc/DSCOverview"))));
const Reports = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-platform/reports")))
);
const ManagePartnerUsers = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-platform/manage-users/ManagePartnerUsers")))
);
const PartnerPlatformBulkOperations = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/partner-platform/bulk-operations/BulkOperations")))
);
const SftpManagement = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/sftp-management/SftpManagement")))
);
const RolesPage = Loadable(lazy(() => componentLoader(() => import("../../pages/spring/roles-management/RolesPage"))));
const PeoplePage = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/roles-management/PeoplePage")))
);
const Gr4vyValidateLogicPage = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/gr4vy-routing/Gr4vyValidateLogicPage")))
);
const RecordingPresign = Loadable(
  lazy(() => componentLoader(() => import("../../pages/spring/recordings/RecordingPresign")))
);

export const backofficeRoutes: RouteObject[] = [
  {
    path: urls.backofficeLogin.path,
    element: <LoginPage />,
  },
  {
    path: urls.backoffice.path,
    element: (
      <BackofficeGuard>
        <BackofficeLayout />
      </BackofficeGuard>
    ),
    children: [
      {
        path: urls.search.path,
        element: <Search />,
      },
      {
        path: urls.informationPanel.path,
        element: <InformationPanel />,
      },
      {
        path: urls.history.path,
        element: <RexHistory />,
      },
      {
        path: urls.targetBreakdown.path,
        element: <TargetBreakdown />,
      },
      {path: "*", element: <Navigate to={urls.error.url} replace />},
      {
        path: "",
        element: <Navigate to={urls.search.url} replace />,
      },
    ],
  },
  {
    path: urls.spring.path,
    element: (
      <BackofficeGuard>
        <SpringLayout />
      </BackofficeGuard>
    ),
    children: [
      {
        path: urls.dscBulkOperations.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_DEBT_SETTLEMENT_BULK_OPERATIONS}>
            <BulkOperations />
          </PermissionGuard>
        ),
      },
      {
        path: urls.importPricing.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_PRICING_IMPORT}>
            <ImportPricing />
          </PermissionGuard>
        ),
      },
      {
        path: urls.generatePricing.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_PRICING_BATCH}>
            <GeneratePricingWashReport />
          </PermissionGuard>
        ),
      },
      {
        path: urls.anonymousCommunications.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_DEBT_SETTLEMENT_ANONYMOUS_COMMS}>
            <AnonymousCommunications />
          </PermissionGuard>
        ),
      },
      {
        path: urls.dscOverview.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_DEBT_SETTLEMENT_OVERVIEW}>
            <DSCOverview />
          </PermissionGuard>
        ),
      },
      {
        path: urls.representationCompanies.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_DEBT_SETTLEMENT_COMPANIES}>
            <RepresentationCompanies />
          </PermissionGuard>
        ),
      },
      {
        path: urls.searchSpring.path,
        element: (
          <PermissionGuard>
            <Search />
          </PermissionGuard>
        ),
      },
      {
        path: urls.informationPanelSpring.path,
        element: <InformationPanel />,
      },
      {
        path: urls.blockedNumbers.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_CALL_CENTER_BLOCKED_NUMBERS}>
            <BlockedNumbers />
          </PermissionGuard>
        ),
      },
      {
        path: urls.rexEmployees.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_CALL_CENTER_REX_SKILLS}>
            <RExEmployees />
          </PermissionGuard>
        ),
      },
      {
        path: urls.rexSkillsLegacy.path,
        element: <Navigate to={urls.rexEmployees.url} replace />,
      },
      {
        path: urls.shiftManagement.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_CALL_CENTER_SHIFT_MANAGEMENT}>
            <ShiftManagement />
          </PermissionGuard>
        ),
      },
      {
        path: urls.simulation.path,
        element: (
          <PermissionGuard>
            <Simulation />
          </PermissionGuard>
        ),
      },
      {
        path: urls.userLevelMedia.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_USER_LEVEL_MEDIA}>
            <UserLevelMedia />
          </PermissionGuard>
        ),
      },
      {
        path: urls.batchLevelMedia.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_BATCH_LEVEL_MEDIA}>
            <BatchLevelMedia />
          </PermissionGuard>
        ),
      },
      {
        path: urls.partners.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <PartnersList />
          </PermissionGuard>
        ),
      },
      {
        path: urls.paymentsCredentials.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <PaymentsCredentialsList />
          </PermissionGuard>
        ),
      },
      {
        path: urls.addPaymentsCredentials.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <AddPaymentsCredentials />
          </PermissionGuard>
        ),
      },
      {
        path: urls.sftpManagement.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_ADMIN_SFTP_MANAGEMENT}>
            <SftpManagement />
          </PermissionGuard>
        ),
      },
      {
        path: urls.gr4vyValidateLogic.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_GR4VY_ROUTING_VALIDATION}>
            <Gr4vyValidateLogicPage />
          </PermissionGuard>
        ),
      },
      {
        path: urls.addPartner.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <AddPartner />
          </PermissionGuard>
        ),
      },
      {
        path: urls.editPartner.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <EditPartner />
          </PermissionGuard>
        ),
      },
      {
        path: urls.accountImports.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_ACCOUNT_IMPORTS}>
            <AccountImports />
          </PermissionGuard>
        ),
      },
      {
        path: urls.accountManagement.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_ACCOUNT_MANAGEMENT}>
            <AccountManagement />
          </PermissionGuard>
        ),
      },
      {
        path: urls.importPayments.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_PAYMENTS}>
            <ImportPayments />
          </PermissionGuard>
        ),
      },
      {
        path: urls.washing.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_OPERATIONS_WASHING}>
            <Washing />
          </PermissionGuard>
        ),
      },
      {
        path: urls.bankruptcies.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_BANKRUPTCIES}>
            <Bankruptcies />
          </PermissionGuard>
        ),
      },
      {
        path: urls.inventory.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_INVENTORY}>
            <Inventory />
          </PermissionGuard>
        ),
      },
      {
        path: urls.legalCollectionsBulkOperation.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_LEGAL_COLLECTIONS_BULK_OPERATIONS}>
            <LegalCollectionsBulkOperation />
          </PermissionGuard>
        ),
      },
      {
        path: urls.disputes.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_COMPLIANCE_DISPUTES}>
            <Disputes />
          </PermissionGuard>
        ),
      },
      {
        path: urls.disputesDashboard.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_COMPLIANCE_OVERVIEW}>
            <DisputesDashboard />
          </PermissionGuard>
        ),
      },
      {
        path: urls.documentExport.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_COMPLIANCE_DOCUMENT_EXPORT}>
            <DocumentExport />
          </PermissionGuard>
        ),
      },
      {
        path: urls.documentRequests.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <DocumentRequests />
          </PermissionGuard>
        ),
      },
      {
        path: urls.partnerPlatformBulkOperation.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <PartnerPlatformBulkOperations />
          </PermissionGuard>
        ),
      },
      {
        path: urls.partnerPlatformReports.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <Reports />
          </PermissionGuard>
        ),
      },
      {
        path: urls.managePartnerUsers.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_PARTNER_MANAGEMENT}>
            <ManagePartnerUsers />
          </PermissionGuard>
        ),
      },
      {
        path: urls.tasks.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_TASKS}>
            <TaskMonitor />
          </PermissionGuard>
        ),
      },
      {
        path: urls.conversationsSpring.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_CALL_CENTER_CONVERSATIONS}>
            <RexHistory />
          </PermissionGuard>
        ),
      },
      {
        path: urls.roles.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_ADMIN_ROLES}>
            <RolesPage />
          </PermissionGuard>
        ),
      },
      {
        path: urls.people.path,
        element: (
          <PermissionGuard permission={PermissionKey.SPRING_ADMIN_PEOPLE}>
            <PeoplePage />
          </PermissionGuard>
        ),
      },
      {
        path: urls.roles.path,
        element: <RolesPage />,
      },
      {
        path: urls.people.path,
        element: <PeoplePage />,
      },
      {
        path: urls.recordingPresign.path,
        element: <RecordingPresign />,
      },
      {
        path: "",
        element: <Navigate to={urls.searchSpring.url} replace />,
      },
    ],
  },
  // Permission Required
  {
    path: urls.permissionRequired.path,
    element: <PermissionRequired />,
  },
];
